import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { debounceTime, distinctUntilChanged, map, skip, Subscription } from 'rxjs';
import { LdgImgComponent } from '@/app/atoms/ldg-img/ldg-img.component';
import { LoadingInlineComponent } from '@/app/atoms/loading-inline/loading-inline.component';
import { ImageService } from '@/app/services/image/image.service';
import { formatBriefTrackerDate } from '../../brief-tracker/brief-tracker.helpers';
import { AdminCreatorStatsService } from '../../../services/admin-creator-stats.service';
import {
  CreatorStatsRowDto,
  CreatorStatsSortField,
  CreatorStatsSortOrder,
} from '../creator-stats.types';

@Component({
  selector: 'admin-tool-creator-stats-list',
  templateUrl: './creator-stats-list.html',
  styleUrl: './creator-stats-list.scss',
  host: { class: 'block h-full w-full min-h-0' },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, InfiniteScrollDirective, LdgImgComponent, LoadingInlineComponent],
})
export class CreatorStatsList implements OnDestroy {
  rows: CreatorStatsRowDto[] = [];
  loadingState: 'idle' | 'loading' | 'loaded' | 'error' = 'idle';
  loadingMore = false;
  error: string | null = null;
  sortField: CreatorStatsSortField = 'appearedInSearches';
  sortOrder: CreatorStatsSortOrder = 'DESC';
  hasMore = true;
  totalCount = 0;
  searchQuery = signal('');

  readonly scrollDistance = 2;
  readonly scrollUpDistance = 1.5;
  readonly throttle = 300;
  readonly formatDate = formatBriefTrackerDate;

  private readonly pageSize = 25;
  private skip = 0;
  private fetchSubscriptionList = new Subscription();
  private readonly searchSubscription = new Subscription();

  private readonly cdr = inject(ChangeDetectorRef);
  private readonly adminCreatorStatsService = inject(AdminCreatorStatsService);
  readonly imageService = inject(ImageService);

  constructor() {
    this.searchSubscription.add(
      toObservable(this.searchQuery)
        .pipe(
          map((query) => query.trim()),
          debounceTime(300),
          distinctUntilChanged(),
          skip(1),
        )
        .subscribe(() => this.loadRows(true)),
    );

    this.loadRows(true);
  }

  ngOnDestroy(): void {
    this.fetchSubscriptionList.unsubscribe();
    this.searchSubscription.unsubscribe();
  }

  creatorLabel(row: CreatorStatsRowDto): string {
    const name = `${row.firstName ?? ''} ${row.lastName ?? ''}`.trim();
    if (name && row.email) {
      return `${name} (${row.email})`;
    }
    return name || row.email || row.creatorId;
  }

  toggleSort(field: CreatorStatsSortField): void {
    if (this.sortField !== field) {
      this.sortField = field;
      this.sortOrder = 'DESC';
    } else {
      this.sortOrder = this.sortOrder === 'ASC' ? 'DESC' : 'ASC';
    }
    this.loadRows(true);
  }

  isSortedBy(field: CreatorStatsSortField): boolean {
    return this.sortField === field;
  }

  get isLoading(): boolean {
    return this.loadingState === 'loading';
  }

  get hasRowData(): boolean {
    return this.rows.length > 0;
  }

  get recordCountLabel(): string {
    if (this.isLoading && this.rows.length === 0) {
      return '—';
    }
    return `${this.rows.length}/${this.totalCount}`;
  }

  onScrollDown(): void {
    if (this.isLoading || this.loadingMore || !this.hasMore) {
      return;
    }
    this.loadRows(false);
  }

  onSearchInput(event: Event): void {
    this.searchQuery.set((event.target as HTMLInputElement).value);
    this.cdr.markForCheck();
  }

  private loadRows(reset: boolean): void {
    if (reset) {
      this.fetchSubscriptionList.unsubscribe();
      this.fetchSubscriptionList = new Subscription();
      this.skip = 0;
      this.hasMore = true;
      this.totalCount = 0;
      this.rows = [];
      this.error = null;
      this.loadingState = 'loading';
      this.loadingMore = false;
    } else if (!this.hasMore || this.loadingMore || this.isLoading) {
      return;
    } else {
      this.loadingMore = true;
    }

    const search = this.searchQuery().trim();
    const body = {
      take: this.pageSize,
      skip: this.skip,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      ...(search ? { search } : {}),
    };

    const sub = this.adminCreatorStatsService.list(body).subscribe({
      next: (result) => {
        const received = Array.isArray(result?.data) ? result.data : [];
        this.totalCount = result?.total ?? this.totalCount;
        this.rows = reset ? received : [...this.rows, ...received];
        this.skip += received.length;
        this.hasMore = this.rows.length < this.totalCount;
        this.loadingState = 'loaded';
        this.loadingMore = false;
        this.cdr.markForCheck();
      },
      error: (err: { error?: { message?: string }; message?: string }) => {
        this.error = err?.error?.message || err?.message || 'Failed to load creator stats.';
        this.loadingState = reset ? 'error' : 'loaded';
        this.loadingMore = false;
        if (!reset) {
          this.hasMore = false;
        }
        this.cdr.markForCheck();
      },
    });

    this.fetchSubscriptionList.add(sub);
  }
}
